import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Trans, useTranslation } from 'react-i18next';
import { FaAngleDown } from 'react-icons/fa';

import { uiHelpers } from '../../helpers';
import {
  getEffectivePromoCode,
  getStripePriceId,
  isMonthRestricted,
  planRestriction,
  STRIPE_ONE_TIME_PRICES,
  STRIPE_ONE_TIME_PRICES_KOL_LEGACY,
  STRIPE_ONE_TIME_PRICES_KOL_LEGACY_SANDBOX,
  STRIPE_ONE_TIME_PRICES_SANDBOX,
  totalAmount,
  usePrices,
  usePromo,
} from '../../helpers/price.helper';
import {
  SandboxBanner,
  useSandboxPayment,
} from '../../hooks/useSandboxPayment';
import { CustomerFacingSubscription } from '../../py-generated/datalouder/membership/types';
import PaymentSelection, { MonthOptions } from './PaymentSelection';
import StripeCheckoutButton from './StripeCheckoutButton';

interface Props {
  subscription: CustomerFacingSubscription;
}

const ExtendSubscriptionView: React.FC<Props> = ({ subscription }) => {
  const { t } = useTranslation();

  const { promo, stripeCoupon } = usePromo();
  const {
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loading: loadingPrices,
  } = usePrices();

  const [month, setMonth] = useState<MonthOptions>(6);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    setMonth(prevMonth =>
      isMonthRestricted(prevMonth, stripeCoupon)
        ? (Number.parseInt(
            planRestriction(stripeCoupon)[0].replace('m', '')
          ) as MonthOptions)
        : prevMonth
    );
  }, [stripeCoupon]);

  const usingSandboxPayment = useSandboxPayment();
  const pricesKolLegacy = usingSandboxPayment
    ? STRIPE_ONE_TIME_PRICES_KOL_LEGACY_SANDBOX
    : STRIPE_ONE_TIME_PRICES_KOL_LEGACY;
  const prices = usingSandboxPayment
    ? STRIPE_ONE_TIME_PRICES_SANDBOX
    : STRIPE_ONE_TIME_PRICES;
  const priceId = promo?.code ? pricesKolLegacy[month] : prices[month];

  if (checkingOut) {
    return (
      <Container>
        <Row>
          <Col className="text-center">
            <br />
            <br />
            <br />
            <h2>{t('checking-out')}</h2>
            <br />
            <br />
            <br />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col md={6}>
          <p>
            <Trans
              i18nKey="subscription-summary-existing"
              values={{
                nextBillTime: uiHelpers.formatDateLocalFromTimestamp(
                  subscription.estimated_next_bill_time
                ),
                cadence: t(
                  `cadence-${subscription.interval_count}${subscription.interval_unit}`
                ),
                price: subscription.renew_price?.toLocaleString(),
              }}
              components={{ bold: <b /> }}
            />
          </p>
          <div className="text-center">
            <Badge bg="secondary">{t('one-time-payment')}</Badge>
            <br />
            <FaAngleDown />
          </div>
          <p>
            <Trans
              i18nKey="subscription-summary-new"
              values={{
                extendDuration: t(`cadence-${month}month`),
                nextBillTime: uiHelpers.formatDateLocalFromTimestamp(
                  DateTime.fromSeconds(subscription.estimated_next_bill_time)
                    .plus({ month })
                    .toSeconds()
                ),
                cadence: t(`cadence-${month}month`),
                price: totalAmount(
                  PRICE_PER_MONTH,
                  DISCOUNT_FOR_12_MONTH,
                  loadingPrices,
                  month,
                  promo,
                  stripeCoupon
                ).toLocaleString(),
              }}
              components={{ bold: <b /> }}
            />
          </p>
        </Col>
        <Col md={6}>
          <PaymentSelection
            freeTrialAvailable={false}
            month={month}
            setMonth={setMonth}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {usingSandboxPayment && <SandboxBanner />}
          <StripeCheckoutButton
            isSandbox={usingSandboxPayment}
            priceId={priceId}
            hasTrial={false}
            promo={getEffectivePromoCode(promo, stripeCoupon)}
            isStripeNativePromo={!!stripeCoupon}
            onCompleted={() => setCheckingOut(true)}
            oneTimePayment
            metadata={{
              extendSubscription: '1',
              promo: getEffectivePromoCode(promo, stripeCoupon),
              subscriptionId: subscription.subscription_id,
              month: month.toString(),
              priceId: getStripePriceId(promo, month, usingSandboxPayment),
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ExtendSubscriptionView;
